import { React, useEffect, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import GppGoodRoundedIcon from '@mui/icons-material/GppGoodRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/nrUpdate.css';

const AtualizarNRs = () => {
    const [funcionario, setFuncionario] = useState(null);
    const [listaFuncionarios, setListaFuncionarios] = useState([]);
    const [funcao, setFuncao] = useState('');
    const [local, setLocal] = useState('');
    const [dataNR10, setDataNR10] = useState(null);
    const [validadeNR10, setValidadeNR10] = useState(null);
    const [errorNR10, setErrorNR10] = useState(false);
    const [dataNR35, setDataNR35] = useState(null);
    const [validadeNR35, setValidadeNR35] = useState(null);
    const [errorNR35, setErrorNR35] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    const filter = createFilterOptions();

    useEffect(() => {
        async function buscarFuncionarios () {
            const response = await fetchFlask('buscarFuncionarios');
            if (response.status !== 'error') {
                setListaFuncionarios(response.dados);
            } else {
                handleAlerta(response);
            }
        };
        buscarFuncionarios();
    }, []);

    async function submitAtualizarNRs (e) {
        e.preventDefault();
        setIsSubmit(true);

        let dataNR10Str = null;
        let validadeNR10Str = null;
        if ((dataNR10 && !validadeNR10) || (!dataNR10 && validadeNR10)) {
            setErrorNR10(true);
            setIsSubmit(false);
            return;
        } else if (dataNR10 && validadeNR10) {
            dataNR10Str = formatIsoDate(dataNR10);
            validadeNR10Str = formatIsoDate(validadeNR10);
        }
        setErrorNR10(false);

        let dataNR35Str = null;
        let validadeNR35Str = null;
        if ((dataNR35 && !validadeNR35) || (!dataNR35 && validadeNR35)) {
            setErrorNR35(true);
            setIsSubmit(false);
            return;
        } else if (dataNR35 && validadeNR35) {
            dataNR35Str = formatIsoDate(dataNR35);
            validadeNR35Str = formatIsoDate(validadeNR35);
        }
        setErrorNR35(false);

        if (!listaFuncionarios.includes(funcionario)) {
            setListaFuncionarios([...listaFuncionarios, funcionario]);
        }

        const body = JSON.stringify({
            funcionario,
            funcao,
            local,
            dataNR10: dataNR10Str,
            validadeNR10: validadeNR10Str,
            dataNR35: dataNR35Str,
            validadeNR35: validadeNR35Str
        });

        try {
            const response = await fetchFlask('atualizarNRs', body);
            handleAlerta(response);
            if (response.status === 'success') {
                setFuncionario(null);
                setFuncao('');
                setLocal('');
                setDataNR10(null);
                setValidadeNR10(null);
                setDataNR35(null);
                setValidadeNR35(null);
            }
        } finally {
            setIsSubmit(false);
        }
    };

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    const hasNR10 = dataNR10 && validadeNR10;
    const hasNR35 = dataNR35 && validadeNR35;
    const completedFields = [
        funcionario,
        funcao,
        local,
        hasNR10,
        hasNR35
    ].filter(Boolean).length;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Atualizar e Adicionar NRs</PageHeader>

            <form method='POST' onSubmit={submitAtualizarNRs} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Controle de validade das certificações NR</h2>
                        </Box>
                        <p>
                            Atualize cadastro funcional e datas de emissão/validade das certificações NR-10 e NR-35.
                        </p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <PersonSearchRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Funcionário</strong>
                            </Box>
                        </Box>

                        <Autocomplete
                            className='nr-update-autocomplete'
                            value={funcionario}
                            options={listaFuncionarios}
                            onChange={(e, value) => setFuncionario(value)}
                            filterOptions={(options, params) => {
                                const filtered = filter(options, params);
                                const { inputValue } = params;
                                const isExisting = options.some((option) => inputValue === option);

                                if (inputValue !== '' && !isExisting) {
                                    filtered.push(inputValue);
                                }

                                return filtered;
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Funcionário'
                                    required
                                />
                            )}
                        />
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <WorkRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Dados funcionais</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <TextField
                                required
                                label='Função'
                                value={funcao}
                                onChange={(e) => setFuncao(e.target.value)}
                            />
                            <TextField
                                required
                                label='Lotação Atual'
                                value={local}
                                onChange={(e) => setLocal(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <GppGoodRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Certificação NR-10</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <FormControl className='ui-control' error={errorNR10}>
                                <DayDatePicker
                                    label='Data NR-10'
                                    minDate={new Date('2023-01-02')}
                                    value={dataNR10}
                                    onChange={setDataNR10}
                                />
                            </FormControl>

                            <FormControl className='ui-control' error={errorNR10}>
                                <DayDatePicker
                                    label='Validade NR-10'
                                    minDate={new Date('2023-01-02')}
                                    value={validadeNR10}
                                    onChange={setValidadeNR10}
                                />
                            </FormControl>
                        </Box>

                        {errorNR10 && (
                            <FormHelperText className='nr-update-error-text'>
                                Se preencher NR-10, informe data e validade.
                            </FormHelperText>
                        )}
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <EventAvailableRoundedIcon />
                            <Box>
                                <span>Etapa 4</span>
                                <strong>Certificação NR-35</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <FormControl className='ui-control' error={errorNR35}>
                                <DayDatePicker
                                    label='Data NR-35'
                                    minDate={new Date('2023-01-02')}
                                    value={dataNR35}
                                    onChange={setDataNR35}
                                />
                            </FormControl>

                            <FormControl className='ui-control' error={errorNR35}>
                                <DayDatePicker
                                    label='Validade NR-35'
                                    minDate={new Date('2023-01-02')}
                                    value={validadeNR35}
                                    onChange={setValidadeNR35}
                                />
                            </FormControl>
                        </Box>

                        {errorNR35 && (
                            <FormHelperText className='nr-update-error-text'>
                                Se preencher NR-35, informe data e validade.
                            </FormHelperText>
                        )}
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão das NRs</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Funcionário</span>
                            <strong className={!funcionario ? 'is-empty' : undefined}>{funcionario || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Função</span>
                            <strong className={!funcao ? 'is-empty' : undefined}>{funcao || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Lotação</span>
                            <strong className={!local ? 'is-empty' : undefined}>{local || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>NR-10</span>
                            <strong className={!hasNR10 ? 'is-empty' : undefined}>{hasNR10 ? `${formatDateLabelPtBr(dataNR10, 'Não informada')} até ${formatDateLabelPtBr(validadeNR10, 'Não informada')}` : 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>NR-35</span>
                            <strong className={!hasNR35 ? 'is-empty' : undefined}>{hasNR35 ? `${formatDateLabelPtBr(dataNR35, 'Não informada')} até ${formatDateLabelPtBr(validadeNR35, 'Não informada')}` : 'Não informada'}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <BadgeRoundedIcon />
                        <p>
                            Cada NR deve ter data de emissão e validade preenchidas em conjunto. Deixe ambas vazias quando não houver atualização.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/5 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Atualizar NRs
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default AtualizarNRs;
